import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Layouts
import MainLayout from "layouts/Main";

// Components
import { Button, InputField, Link, Notification } from "@octopus-investments/deep-sea-components";
import PasswordField from "components/Inputs/PasswordField/PasswordField";

// Actions
import { sendNewEmail, signIn } from "store/slices/accountSlice";
import {
  setBackButtonTarget,
  setContent,
  setEmailNotSent,
  setResetPassword,
  setPasswordForTwoFactor,
} from "store/slices/applicationSlice";
import { setLoginButtonLoader, setResendButtonLoader } from "store/slices/loadersSlice";

// Tools
import { identifyUserInTrackingSystem, getStatusForAmpli } from "utils/helpers";

// Tracking
import { ampli } from "ampli";

// Styles
import "./LoginPage.scss";

export default function LoginPage() {
  const { isLoginButtonLoader } = useSelector(({ loaders }) => loaders);
  const { email } = useSelector(({ application }) => application);
  const { isTwoFactorEnabled } = useSelector(({ application }) => application);

  const [showError, setShowError] = useState(false);
  const [password, setPassword] = useState("");

  // Redux
  const reduxDispatch = useDispatch();

  function handlePasswordChange(e) {
    setPassword(e.value);
  }

  function handlePasswordKeyPress(e) {
    if (e.which === 13) handleLoginButton();
  }

  function handleBackButton() {
    ampli.backButtonClicked({
      di_email_entered: email,
      location: "di_login_page",
    });

    reduxDispatch(setContent("loginOrRegister"));
  }

  function handleLoginButton() {
    if (isLoginButtonLoader) return;

    reduxDispatch(setLoginButtonLoader(true));

    reduxDispatch(signIn({ email, password })).then(result => {
      const { isTemporaryLocked, status, success } = result.payload;

      if (isTwoFactorEnabled) {
        ampli.loginNextClicked({
          di_email_entered: email,
          di_status: getStatusForAmpli({ isTemporaryLocked, status }),
          mfa_status: isTwoFactorEnabled,
        });
      } else {
        ampli.loginIntended({
          di_email_entered: email,
          di_status: getStatusForAmpli({ isTemporaryLocked, status }),
          mfa_status: isTwoFactorEnabled,
        });
      }

      if (success) {
        if (isTwoFactorEnabled) {
          reduxDispatch(setPasswordForTwoFactor(password));
          reduxDispatch(setContent("twoFactorLogin"));
          return reduxDispatch(setLoginButtonLoader(false));
        } else {
          identifyUserInTrackingSystem({ source: "login-page" });
          const urlParams = new URLSearchParams(window.location.search);
          const isRedirect = urlParams.has("returnUrl");
          isRedirect
            ? (window.location.href = urlParams.get("returnUrl"))
            : (window.location.href = process.env.REACT_APP_WELCOME_BTN_URL);
        }
      } else {
        setShowError(true);
        return reduxDispatch(setLoginButtonLoader(false));
      }
    });
  }

  const handleClickToResetLink = () => {
    handleForgotPasswordButton();
  };

  function handleForgotPasswordButton() {
    if (isLoginButtonLoader) return;

    reduxDispatch(setResetPassword(true));
    reduxDispatch(setResendButtonLoader(true));
    reduxDispatch(setContent("checkMail"));
    reduxDispatch(setBackButtonTarget("login"));

    const returnUrl = new URLSearchParams(window.location.search).get("returnUrl");

    reduxDispatch(sendNewEmail({ returnUrl })).then(result => {
      const { success } = result.payload;

      if (success) {
        ampli.forgotPasswordClicked({
          di_email_entered: email,
          location: "di_login_page",
        });
      } else {
        ampli.forgotPasswordClicked({
          di_email_entered: email,
          location: "di_login_page_error",
        });

        reduxDispatch(setEmailNotSent(true));
        return reduxDispatch(setResendButtonLoader(false));
      }
    });
  }

  return (
    <MainLayout>
      <div className="login-content">
        <div className="login-form">
          <h1 className="coheadline navyBlue ta-l">Enter your password</h1>

          <div className="enter-your-password-block ta-l">
            {showError && (
              <div className="error-message">
                <Notification
                  animation="none"
                  message={
                    <>
                      Sorry, we can't find your account. Please double check your details, or{" "}
                      <Link isInline onLinkClick={handleClickToResetLink}>
                        reset your password here
                      </Link>
                      .
                    </>
                  }
                  showNotification
                  status="error"
                  type="inline"
                />
              </div>
            )}
            <InputField
              className="client-email"
              value={email}
              disabled
              id="loginPageEmailInput"
              label="Email address"
              name="email"
            />
            <PasswordField
              autoFocus
              className="client-password"
              disabled={isLoginButtonLoader}
              id="loginPagePasswordInput"
              label="Password"
              name="password"
              onChange={handlePasswordChange}
              onKeyPress={handlePasswordKeyPress}
              value={password}
            />
            <div className="forgot-password-block">
              <Link onClick={handleForgotPasswordButton}>Forgot password?</Link>
            </div>
            <div className="buttons-block">
              <Button label="Back" onClick={handleBackButton} variant="outline" />
              <Button
                isLoading={isLoginButtonLoader}
                label={isTwoFactorEnabled ? "Next" : "Login"}
                onClick={handleLoginButton}
                variant="solid"
              />
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
